import { z } from 'zod';

// ─── The https-only URL rule, in one place ───
//
// Extracted from `task.schema.ts`'s `ExternalUrlSchema` (#738) when the custom-field
// URL type (#703) became a second surface storing a user-supplied link. The rule is a
// SECURITY rule, so it gets one definition rather than two that agree today: the whole
// schema is shared, not merely the protocol predicate, because a surface that
// re-implemented the wiring could forget `.trim()` or the length cap and still look
// like it was reusing the rule.

/**
 * Longest URL we will store. Matches `Task.externalUrl`'s `NVarChar(2048)`; the custom-field
 * URL type shares the cap even though its column is `NVarChar(Max)`, because a link longer
 * than this is a payload rather than a link.
 */
export const HTTPS_URL_MAX = 2048;

/**
 * True when `value` is an `https:` URL with a host.
 *
 * 🚨 THE OBVIOUS VALIDATOR DOES NOT CLOSE THIS. `z.string().url()` accepts
 * `javascript:alert(1)` and `data:text/html,…` — both are perfectly well-formed URLs. These
 * values get rendered as clickable `href`s, so a stored `javascript:` URL defended against
 * only by the renderer is one careless `href={value}` away from firing, in a component
 * nobody remembers is dangerous. The scheme is therefore checked against `URL.protocol`
 * rather than against url-ness (owner ruling 2026-08-11: `https:` only, no host allowlist).
 *
 * The `hostname` check is deliberate defence-in-depth rather than a live guard, and this
 * comment says so instead of implying more: for SPECIAL schemes (http/https/ws/wss/ftp/file)
 * the WHATWG parser guarantees a host — `https:evil` normalises to `https://evil/`, and
 * `https://` throws — so no `https:` value can reach it empty-handed. It exists so that
 * widening the accepted scheme set later (a non-special scheme CAN be hostless) does not
 * silently open that door.
 */
export function isHttpsUrl(value: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false;
  }
  return parsed.protocol === 'https:' && parsed.hostname.length > 0;
}

/**
 * Build an https-only URL validator carrying a surface-specific message key.
 *
 * The key is a stable KEY, not prose (Batch B7 pattern A) — the client maps it to localized
 * text; the API returns it verbatim. Each surface passes its own so the error names the field
 * the user is actually looking at, while the rule itself stays single-sourced.
 *
 * @param messageKey - i18n key returned when the value is not an https URL.
 */
export function httpsUrlSchema(messageKey: string): z.ZodEffects<z.ZodString, string, string> {
  return z.string().trim().max(HTTPS_URL_MAX).refine(isHttpsUrl, { message: messageKey });
}
